/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from '@msdyn365-commerce-modules/checkout';
import { ICheckoutProps, ICheckoutResources } from '../definition-extensions/checkout.ext.props.autogenerated';
import { Collapse, CardBody, Card, CardHeader } from 'reactstrap';
import { getCartState } from '@msdyn365-commerce/global-state';
import { ICoreContext } from '@msdyn365-commerce/core';
const NUMBER_OF_ITEMS = 'ITEM_NUMBERS';

interface IOrderSummaryExtended {
    lineItems?: ILineItems;
    resources: ICheckoutResources;
    props: ICheckoutViewProps & ICheckoutProps<{}>;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
        <Node tag='div' className='msc-checkout__edit-link'>
            {editLink}
        </Node>
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary & IOrderSummaryExtended> = ({ heading, lines, lineItems, resources, props }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    // NOTE: Need to check for other shipment products (ship, email, pickup)
    const itemsCount = props.data.checkout.result?.checkoutCart.cart.CartLines?.length;
    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.tax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
            <Node className='msc-order-summary__items-accordion' tag='div'>
                <CardHeader
                    tabIndex={0}
                    className={isOpen ? 'active' : ''}
                    onClick={() => setIsOpen(prevSt => !prevSt)}
                    onKeyPress={() => setIsOpen(prevSt => !prevSt)}
                >
                    {resources.itemsInCurrentOrderLabel}
                    {itemsCount}
                </CardHeader>
                <Collapse isOpen={isOpen}>
                    <Card>
                        <CardBody>{lineItems && <LineItemsComponent {...lineItems} />}</CardBody>
                    </Card>
                </Collapse>
            </Node>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);
const getShowButtonFrmCart = async (
    context: ICoreContext<{
        [x: string]: any;
    }>
): Promise<boolean | undefined> => {
    const cartState = await getCartState(context.actionContext);
    return cartState?.cart.CartLines && cartState?.cart?.TotalAmount === 0;
};
const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<{}>> = props => {
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        context
    } = props;
    React.useEffect(() => {
        const storageName = `${props.context.request.user.customerAccountNumber}-${NUMBER_OF_ITEMS}`;
        localStorage?.setItem(storageName, JSON.stringify(lineItems?.itemsForShip?.lineItems?.length));
    }, [lineItems?.itemsForShip?.lineItems?.length]);

    const [showButtonFrmCart, setShowButtonFrmCart] = React.useState<boolean>(false);
    getShowButtonFrmCart(context).then(res => setShowButtonFrmCart(res!));

    const getPlaceOrderButton = () => {
        return props.config.showPlaceOrderButton ? (
            <Node {...mainControlProps}>{placeOrderButton}</Node>
        ) : (
            showButtonFrmCart && <Node {...mainControlProps}>{placeOrderButton}</Node>
        );
    };
    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                {getPlaceOrderButton()}
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && (
                                          <OrderSummaryComponent
                                              {...orderSummary}
                                              lineItems={lineItems}
                                              resources={props.resources}
                                              props={props}
                                          />
                                      )
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                </Node>
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {/* {getPlaceOrderButton()} */}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
